import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  ElementRef,
  ViewChild,
  ChangeDetectionStrategy,
  DestroyRef,
  Input,
  AfterViewInit,
  OnDestroy,
  NgZone,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, throttleTime } from 'rxjs';
import {
  CommonPaneComponent,
  TCommonPaneAppearance,
} from '@prlw/common/common-pane/common-pane.component';

export type TItem = {
  dateLabel: string | null;
  title: string;
  url: string;
  source: string | null;
};

@Component({
  selector: 'prlw-text-marquee-ui',
  imports: [CommonPaneComponent, NgTemplateOutlet],
  templateUrl: './text-marquee.component.html',
  styleUrl: './text-marquee.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextMarqueeComponent implements AfterViewInit, OnDestroy {
  @Input() appearance: TCommonPaneAppearance = null;
  @Input() items: TItem[] | null = null;

  @ViewChild('marqueeContent') marqueeContent!: ElementRef<HTMLDivElement>;
  @ViewChild('marqueeContainer') marqueeContainer!: ElementRef<HTMLDivElement>;

  private contentWidth = 0;
  private isHovered = false;
  private readonly speed = 1.3;
  private readonly hoverSpeed = 0.5;
  private animation: Animation | null = null;
  private animationDuration = 0;

  private readonly onWheelBound = (event: WheelEvent) => {
    event.preventDefault();
    this.wheel$.next(event);
  };

  private readonly onMouseEnterBound = () => {
    this.isHovered = true;
    if (this.animation) {
      this.animation.playbackRate = this.hoverSpeed / this.speed;
    }
  };

  private readonly onMouseLeaveBound = () => {
    this.isHovered = false;
    if (this.animation) {
      this.animation.playbackRate = 1;
    }
  };

  private readonly wheel$ = new Subject<WheelEvent>();

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly ngZone: NgZone,
  ) {}

  ngAfterViewInit() {
    this.ngZone.runOutsideAngular(() => {
      this.calculateWidths();
      this.startAutoScroll();

      const container = this.marqueeContainer?.nativeElement;
      container?.addEventListener('wheel', this.onWheelBound, {
        passive: false,
      });
      container?.addEventListener('mouseenter', this.onMouseEnterBound);
      container?.addEventListener('mouseleave', this.onMouseLeaveBound);
      window.addEventListener('resize', this.onResizeBound);

      this.wheel$
        .pipe(throttleTime(40), takeUntilDestroyed(this.destroyRef))
        .subscribe((event) => {
          const scrollAmount = (event.deltaY || event.deltaX) * 1.5;
          this.scrollBy(scrollAmount);
        });
    });
  }

  ngOnDestroy() {
    this.animation?.cancel();
    this.animation = null;
    this.animationDuration = 0;

    const container = this.marqueeContainer?.nativeElement;
    container?.removeEventListener('wheel', this.onWheelBound);
    container?.removeEventListener('mouseenter', this.onMouseEnterBound);
    container?.removeEventListener('mouseleave', this.onMouseLeaveBound);
    window.removeEventListener('resize', this.onResizeBound);
  }

  truncate(text: string, length = 100): string {
    if (!text) return '';
    return text.length > length ? `${text.slice(0, length)}...` : text;
  }

  private calculateWidths() {
    if (this.marqueeContent?.nativeElement) {
      const fullWidth = this.marqueeContent.nativeElement.scrollWidth;
      this.contentWidth = fullWidth / 2;
    }
  }

  private onResizeBound = () => {
    this.calculateWidths();
    this.startAutoScroll();
  };

  private startAutoScroll() {
    const element = this.marqueeContent?.nativeElement;
    if (!element || this.contentWidth <= 0) {
      return;
    }

    const progress = this.getLoopProgress();
    this.animation?.cancel();

    // speed is px per ~16ms frame → px/s
    const pixelsPerSecond = (this.speed * 1000) / 16;
    const duration = (this.contentWidth / pixelsPerSecond) * 1000;
    this.animationDuration = duration;

    this.animation = element.animate(
      [
        { transform: 'translate3d(0px, 0, 0)' },
        { transform: `translate3d(${-this.contentWidth}px, 0, 0)` },
      ],
      {
        duration,
        iterations: Infinity,
        easing: 'linear',
      },
    );

    this.animation.currentTime = progress * duration;
    this.animation.playbackRate = this.isHovered
      ? this.hoverSpeed / this.speed
      : 1;
  }

  private getLoopProgress(): number {
    if (!this.animation || !this.contentWidth) {
      return 0;
    }

    const duration = this.animationDuration;
    if (!duration) {
      return 0;
    }

    const currentTime = Number(this.animation.currentTime ?? 0);
    return this.normalizeTime(currentTime, duration) / duration;
  }

  private scrollBy(scrollAmount: number) {
    if (!this.animation || !this.contentWidth) {
      return;
    }

    const duration = this.animationDuration;
    if (!duration) {
      return;
    }

    const currentTime = Number(this.animation.currentTime ?? 0);
    const deltaTime = (scrollAmount / this.contentWidth) * duration;
    this.animation.currentTime = this.normalizeTime(
      currentTime + deltaTime,
      duration,
    );
  }

  private normalizeTime(time: number, duration: number): number {
    return ((time % duration) + duration) % duration;
  }
}
